import { useRollbar } from '@rollbar/react'
import { usePage } from 'client/store'
import {
  removePopup as removePopupAction,
  toggleActivePopup,
} from 'client/store/management/managementActions'
import { createPopupStructure as createPopupStructureAction } from 'client/store/page/pageActions'
import pageSelectors from 'client/store/page/pageSelectors'
import { pageTypes } from 'common/constants/pageTypes'
import { EntityTypeEnum } from 'common/enums/entityTypeEnum'
import {
  BaseEntityInterface,
  EntityInnerItemInterface,
} from 'common/types/entities/EntityInterface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { PopupInterface } from 'common/types/entities/popup-interface'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { PlusIcon } from 'src/editor/shared/icons/plus-icon'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import { createPopupFromLibrary } from '../library/creators/create-popup'
import { createPopupWebinarRegistrationFromLibrary } from '../library/creators/create-popup-webinar-registration'
import { createPopupWithAgreementFromLibrary } from '../library/creators/create-popup-with-agreement'
import { PopupItem } from './popup-item'

export const PopupManagement = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const rollbar = useRollbar()

  const popups = usePage(pageSelectors.getPopupEntities)
  const pageType = usePage(pageSelectors.getPageType)
  const pageLocale = usePage(pageSelectors.getPageLocale)
  const nextPopupNumber = usePage(pageSelectors.getNextPopupNumber)

  const showPopup = (id: string) => dispatch(toggleActivePopup(id))
  const removePopup = (id: string) => dispatch(removePopupAction(id))
  const createPopupStructure = (structure: {
    [id: string]:
      | EntityInnerItemInterface
      | BaseEntityInterface
      | PopupInterface
      | OldEntityInterface
  }) => dispatch(createPopupStructureAction(structure))

  const createPopup = () => {
    let popup: {
      ascendantId: string
      structure: {
        [id: string]:
          | EntityInnerItemInterface
          | BaseEntityInterface
          | PopupInterface
          | OldEntityInterface
      }
    } | null = null
    if (pageType === pageTypes.offerPage || pageType === pageTypes.offerInner) {
      popup = createPopupWithAgreementFromLibrary(pageLocale)
    } else if (pageType === pageTypes.webinarRegistration) {
      popup = createPopupWebinarRegistrationFromLibrary(pageLocale)
    } else {
      popup = createPopupFromLibrary(pageLocale)
    }

    if (!popup) {
      return rollbar.error('Popup creation failed')
    }
    ;(popup.structure[popup.ascendantId] as PopupInterface).title =
      `Pop-up ${nextPopupNumber}`
    createPopupStructure(popup.structure)
    showPopup(popup.ascendantId)
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <span className="text-heading-4">{t('popup_management.pop-ups')}</span>
        <GhostIconButton
          onClick={createPopup}
          icon={<PlusIcon />}
          title={t('popup_management.create_page')}
        />
      </div>
      {popups.length === 0 ? (
        <ManagementEmpty />
      ) : (
        <div className="flex flex-col gap-1">
          <span className="mb-3 text-M text-textTertiary">
            {t('popup_management.page_type')}
          </span>
          {popups.map(popupEntity => (
            <PopupItem
              id={popupEntity.id}
              title={
                popupEntity.type === EntityTypeEnum.Popup
                  ? popupEntity.title
                  : popupEntity?.options?.title
              }
              display={showPopup}
              remove={removePopup}
              key={popupEntity.id}
            />
          ))}
        </div>
      )}
    </>
  )
}

function ManagementEmpty() {
  const { t } = useTranslation()

  return (
    <div className="mt-12 text-center text-textTertiary">
      <span className="text-S">{t('popup_management.empty_popups')}</span>
    </div>
  )
}
